import React, { useMemo } from "react";
import { IconButton, Tooltip } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import VisibilityIcon from "@material-ui/icons/Visibility";
import Table from "../../../components/Table/Table";
import { Header } from "../../../components";
import { PerformerApprovedBaseColumns } from "../../../utils/utils";
import TicketButton from "../../../components/TableComponents/TicketButton/TicketButton";
import StatusBadge from "../../../components/TableComponents/StatusBadge/StatusBadge";
import TableLayout from "../../../layouts/TableLayout/TableLayout";
import useTicketData from "../../../hooks/useTicketData";
import DocumentViewerModal from "../../../components/DocumentViewerModal/DocumentViewerModal";
import useDocumentViewer from "../../../hooks/useDocumentViewer";
import useTicketExport from "../../../hooks/useTicketExport";
import NoItemsFound from "../../../components/NoItemsFound/NoItemsFound";

function Approved() {
  const {
    searchQuery,
    allRows: filteredRows,
    loading,
    isRefreshing,
    handleRefresh,
    handleSearchQuery,
    total,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTicketData("E_VERIFICATION", true);

  const {
    documentViewerModal,
    handleDownloadFile,
    handleViewDocument,
    handleCloseDocumentViewer,
  } = useDocumentViewer();

  const columns = useMemo(() => {
    const updatedColumns = [...PerformerApprovedBaseColumns];

    updatedColumns[0] = {
      width: 180,
      title: "Ticket Number",
      field: "ticketNumber",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <TicketButton
          ticketNumber={rowData.ticketNumber}
          ticketId={rowData.id}
        />
      ),
    };

    updatedColumns[1] = {
      width: 180,
      title: "Status",
      field: "status",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => <StatusBadge status={rowData.status} />,
    };

    updatedColumns[3] = {
      width: 200,
      title: "Time Stamp",
      field: "timeStamp",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => {
        const value = rowData.updatedOn;
        return (
          <div>{value ? new Date(value).toLocaleString() : "Not Received"}</div>
        );
      },
    };

    updatedColumns[7] = {
      width: 220,
      title: "Entitlement Letter",
      field: "entitlementLetter",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span>{rowData.entitlementLetter}</span>
          <Tooltip title="Downloaded">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                handleDownloadFile(rowData.ticketNumber);
              }}
            >
              <GetAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewDocument(rowData.ticketNumber)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    };

    return updatedColumns;
  }, [handleDownloadFile, handleViewDocument]);

  const { handleExport, isExporting } = useTicketExport(
    "E_VERIFICATION",
    columns
  );

  return (
    <TableLayout
      styles={{ tableInnerWrapper: { paddingBottom: "0rem" } }}
      header={
        <Header
          searchQuery={searchQuery}
          handleSearchQuery={handleSearchQuery}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          onExport={handleExport}
          disableExport={filteredRows.length === 0}
          isExporting={isExporting}
        />
      }
    >
      {filteredRows.length === 0 ? (
        <NoItemsFound />
      ) : (
        <Table
          columns={columns}
          defaultColumns={columns}
          rows={filteredRows}
          hideCheckbox
          rowHeight={45}
          setHasActiveEdits={() => {}}
          isDataLoading={loading || isRefreshing}
          serverSide
          totalRows={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      <DocumentViewerModal
        open={documentViewerModal.open}
        onClose={handleCloseDocumentViewer}
        documentUrl={documentViewerModal.documentUrl}
        documentName={documentViewerModal.documentName}
        documentType={documentViewerModal.documentType}
        isLoading={documentViewerModal.isLoading}
        error={documentViewerModal.error}
      />
    </TableLayout>
  );
}

export default Approved;
